import * as React from "react";
import cx from "classnames";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { useAuth } from "../../../auth";
import { isLeadership } from "../../../lib/access";
import BaseSidebar from "../../../components/sidebar";
import { getPath } from "../../../resources/router/router.selectors";
import { IStore } from "../../../resources/types";

import styles from "./sidebar.module.css";

interface IMenuItem {
  key: string;
  title: string;
  icon?: React.ReactNode;
  children?: IMenuItem[];
  /**
   * When true, only render this item for pilot leadership users (see
   * src/lib/access.ts). Used to hide the coach dashboard from coach-tier
   * users while the dashboard's analytics API key is bundled in the JS.
   */
  leadershipOnly?: boolean;
}

const sidebarMenu: IMenuItem[] = [
  {
    key: "/dashboard",
    title: "Dashboard",
    leadershipOnly: true
  },
  {
    key: "/users",
    title: "Users"
  }
];

function isActive(path: string, key: string): boolean {
  return path === key || path.startsWith(`${key}/`);
}

interface IProps {
  className?: string;
  isOpen: boolean;
  onClose: () => any;
  path: string;
}

function Sidebar({ isOpen, onClose, path, className }: IProps) {
  const { logout, user } = useAuth();
  const leadership = isLeadership(user?.email);

  const items = sidebarMenu
    .filter(item => !item.leadershipOnly || leadership)
    .map(item => {
      if (!item.children || !item.children.length) {
        return (
          <li
            key={item.key}
            role="menuitem"
            className={cx(styles.menuItem, {
              [styles.active]: isActive(path, item.key)
            })}
          >
            <Link to={item.key}>
              {item.icon}
              {item.title}
            </Link>
          </li>
        );
      }

      const submenu = item.children.map(subitem => {
        return (
          <li
            key={subitem.key}
            role="menuitem"
            className={cx(styles.menuItem, {
              [styles.active]: path.includes(subitem.key)
            })}
          >
            <Link to={subitem.key}>
              {subitem.icon}
              {subitem.title}
            </Link>
          </li>
        );
      });

      return (
        <li className={cx(styles.menuItem, styles.hasChildren)} key={item.key}>
          <Link to={item.key}>
            {item.icon}
            {item.title}
          </Link>
          <ul className={styles.submenu}>{submenu}</ul>
        </li>
      );
    });

  return (
    <BaseSidebar
      className={className}
      isOpen={isOpen}
      onClose={onClose}
      path={path}
    >
      <ul className={styles.menu} role="menu">
        {items}
      </ul>
      <div className={styles.footer}>
        {/* eslint-disable-next-line */}
        <a onClick={logout}>Log out</a>
      </div>
    </BaseSidebar>
  );
}

export default connect((state: IStore) => ({
  path: getPath(state)
}))(Sidebar);
